import { featuredApi, featuredApiEffects } from 'modules/Featured/components/FeaturedItems/apiEffects';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { pipe, either } from 'utils/fp';
import { Thumbnail } from 'components/Thumbnail';
import { IconProps } from 'components/Icons/IconProps';
import { useQuery } from 'react-query';
import { SetupComputeType, TrendingModel, UserIdToIconMapping } from '../constants';
import { appsListIcon } from '../CodeSnippet/CodeSteps.styles';

export function useTrendingModelsTE() {
  const { data: featuredModelsE } = useQueryTE([queryKeys.Models, { featured: true }], featuredApiEffects.listFeaturedModelsTE(), {
    staleTime: 1000 * 60,
  });

  return pipe(
    featuredModelsE,
    either.map((featuredModels: CF.API.Models.Model[]) => {
      if (!featuredModels) return [];

      return featuredModels
        .filter(({ model_version: { inference_compute_info } }) => inference_compute_info)
        .map((model) => {
          return convertToTrendingModel(model);
        });
    }),
  );
}

export function useTrendingModels() {
  const { data: models, isLoading } = useQuery([queryKeys.FeaturedModels, { featured: true }], () => featuredApi.listFeaturedModels(), {
    staleTime: 1000 * 60,
  });

  const trendingModels =
    models
      ?.filter(({ model_version: { inference_compute_info } }) => inference_compute_info?.accelerator_type)
      .map((model) => {
        return convertToTrendingModel(model);
      }) ?? ([] as TrendingModel[]);
  return { trendingModels, isLoading };
}

function getModelSupportedComputeTypes(model: CF.API.Models.Model): SetupComputeType[] {
  const basicGPU = 'NVIDIA-A10G';
  const advancedGPU = 'NVIDIA-L40S';
  const allGPU = 'NVIDIA-*';

  if (!model.model_version.inference_compute_info?.accelerator_type) {
    return [SetupComputeType.Basic, SetupComputeType.Advanced];
  }

  if (model.model_version.inference_compute_info?.accelerator_type.indexOf(basicGPU) > -1) {
    return [SetupComputeType.Basic, SetupComputeType.Advanced];
  }

  if (model.model_version.inference_compute_info?.accelerator_type.indexOf(allGPU) > -1) {
    return [SetupComputeType.Basic, SetupComputeType.Advanced];
  }

  if (model.model_version.inference_compute_info?.accelerator_type.indexOf(advancedGPU) > -1) {
    return [SetupComputeType.Advanced];
  }

  return [SetupComputeType.Advanced];
}

function convertToTrendingModel(model: CF.API.Models.Model): TrendingModel {
  const convertedModel: TrendingModel = {
    modelId: model.id,
    userId: model.user_id,
    appId: model.app_id,
    icon: (props) => <ModelIcon model={model} {...props} />,
    title: model.name,
    body: shortDescription(model),
    modelVersionId: model.model_version?.id,
    supportedComputeTypes: getModelSupportedComputeTypes(model),
    modelData: model,
  };

  return convertedModel;
}

function ModelIcon({ model, ...props }: { model: CF.API.Models.Model } & IconProps) {
  if (Object.keys(UserIdToIconMapping).includes(model.user_id)) {
    const Icon = UserIdToIconMapping[model.user_id as keyof typeof UserIdToIconMapping];
    return <Icon size={36} {...props} />;
  }

  return <DefaultModelIcon model={model} />;
}

function DefaultModelIcon({ model }: { model: CF.API.Models.Model }) {
  return <Thumbnail fallback={model.id} data-testid="model-{model.id}-thumbnail" className={appsListIcon} />;
}

function shortDescription(model: CF.API.Models.Model) {
  const description = model.description || '';
  return description.length > 100 ? `${description.slice(0, 100)}...` : description;
}
